/**
 * Public leadership About page — the congregation can browse who serves where.
 *
 * No auth. Contact details only appear when an admin has published them.
 * GET /api/public/leadership?organisation=&organisation_type=&search=
 */
import React, { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Search, Mail, Phone, Globe, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublicLeadershipPage = () => {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const organisation = slug || params.get('organisation') || '';
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [lightbox, setLightbox] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['public-leadership', organisation, search, type],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (organisation) qs.set('organisation', organisation);
      if (search) qs.set('search', search);
      if (type) qs.set('organisation_type', type);
      const res = await fetch(`${API_URL}/api/public/leadership?${qs.toString()}`);
      if (!res.ok) throw new Error('Could not load the leadership directory');
      return res.json();
    },
  });

  const leaders = data?.leaders || [];

  return (
    <div className="min-h-screen bg-[#0f1115] text-slate-100" data-testid="public-leadership-page">
      <header className="border-b border-white/10 bg-gradient-to-b from-[#161a22] to-[#0f1115]">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <p className="text-xs uppercase tracking-[0.3em] text-amber-400/80">Leadership</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold mt-3 leading-tight"
              data-testid="public-leadership-title">
            {data?.scope || 'Our leadership'}
          </h1>
          <p className="text-base text-slate-400 mt-4 max-w-2xl">
            The men and women who serve across our ministry — where they serve, and what they
            carry. {data?.count ? `${data.count} leader${data.count === 1 ? '' : 's'} listed.` : ''}
          </p>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10 space-y-8">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative max-w-sm flex-1 min-w-0 sm:min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search a name, title or place…"
              data-testid="public-leadership-search"
              className="pl-10 bg-white/5 border-white/10 text-slate-100 placeholder:text-slate-500"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setType('')} data-testid="public-leadership-filter-all"
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                type === '' ? 'bg-amber-500 text-amber-950' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`}
            >
              Everyone
            </button>
            {(data?.types || []).map((t) => (
              <button
                key={t.key} onClick={() => setType(t.key)}
                data-testid={`public-leadership-filter-${t.key}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  type === t.key ? 'bg-amber-500 text-amber-950' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-52 rounded-xl bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : leaders.length === 0 ? (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] py-20 text-center"
               data-testid="public-leadership-empty">
            <Users className="h-8 w-8 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-300">No leaders published yet.</p>
            <p className="text-sm text-slate-500 mt-1">
              Once the office publishes the leadership list, it will appear here.
            </p>
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {leaders.map((l, i) => (
              <article
                key={l.organisation_id}
                data-testid={`public-leader-${l.organisation_id}`}
                style={{ animationDelay: `${Math.min(i, 8) * 60}ms` }}
                className="group rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden hover:border-amber-400/40 hover:bg-white/[0.06] transition-colors"
              >
                <div className="p-5 flex gap-4">
                  {l.leader_photo_url ? (
                    <img src={l.leader_photo_url} alt={l.leader_name}
                         onClick={() => setLightbox(l.leader_photo_url)}
                         className="h-16 w-16 rounded-full object-cover ring-1 ring-white/15 cursor-zoom-in shrink-0" />
                  ) : (
                    <span className="h-16 w-16 rounded-full bg-white/10 text-slate-300 grid place-items-center font-semibold shrink-0">
                      {(l.leader_name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('')}
                    </span>
                  )}
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-50 truncate">{l.leader_name}</p>
                    <p className="text-xs text-amber-400/90">{l.leader_title}</p>
                    <p className="text-[11px] text-slate-500 mt-1 truncate" title={l.path}>{l.path}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      <Badge variant="outline"
                             className="text-[10px] border-white/15 text-slate-300">{l.type_label}</Badge>
                      {(l.countries || []).slice(0, 2).map((c) => (
                        <Badge key={c} variant="outline"
                               className="text-[10px] border-sky-400/30 text-sky-300">{c}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
                {l.leader_bio && (
                  <p className="px-5 pb-4 text-sm text-slate-400 whitespace-pre-line line-clamp-5">
                    {l.leader_bio}
                  </p>
                )}
                {(l.leader_photos || []).length > 0 && (
                  <div className="px-5 pb-4 flex gap-2 flex-wrap">
                    {l.leader_photos.slice(0, 4).map((url) => (
                      <img key={url} src={url} alt="" onClick={() => setLightbox(url)}
                           className="h-12 w-12 rounded-md object-cover ring-1 ring-white/10 cursor-zoom-in" />
                    ))}
                  </div>
                )}
                {(l.email || l.phone || l.website_url) && (
                  <div className="px-5 py-3 border-t border-white/10 flex flex-wrap gap-4 text-xs">
                    {l.email && (
                      <a href={`mailto:${l.email}`} className="text-slate-300 hover:text-amber-400 flex items-center gap-1">
                        <Mail className="h-3 w-3" /> {l.email}
                      </a>
                    )}
                    {l.phone && (
                      <a href={`tel:${l.phone}`} className="text-slate-300 hover:text-amber-400 flex items-center gap-1">
                        <Phone className="h-3 w-3" /> {l.phone}
                      </a>
                    )}
                    {l.website_url && (
                      <a href={l.website_url} target="_blank" rel="noreferrer"
                         className="text-slate-300 hover:text-amber-400 flex items-center gap-1">
                        <Globe className="h-3 w-3" /> Website
                      </a>
                    )}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}

        <p className="text-xs text-slate-600 pt-6 border-t border-white/10">
          Contact details appear only where a leader has agreed to publish them.
        </p>
      </div>

      <Dialog open={!!lightbox} onOpenChange={(v) => !v && setLightbox('')}>
        <DialogContent className="max-w-3xl p-2" data-testid="public-leader-lightbox">
          {lightbox && <img src={lightbox} alt="" className="w-full rounded-md" />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicLeadershipPage;
